/** Guest journey → Self-registration (plan §2 S5, B5; Plan B "links" B3): `allow_selfreg` and
 *  the two verification switches on the draft, then the tour's self-registration links.
 *
 *  - The links (`selfreg/SelfregLinks`) need console_tours_manage: a link lets anyone join
 *    the tour, so a viewer is told how many tour-wide links there are
 *    (`selfreg_link_count`), never the links.
 *  - Every link action (create, edit settings, make master, revoke, restore) is immediate
 *    and audited server-side — none touches the draft, so all are offered in both modes,
 *    in the same place (Draft-Or-Now, Zero-Re-Layout).
 *  - Imports reset the verification switches on every run unless the XML sets them — said
 *    under the switches, in both modes. With self-registration off, a link opens the
 *    operator's general page without this tour — said beside the links. */
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Typography } from '@mui/material';

import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';
import { usePermissions } from '@/hooks/usePermissions';

import { useTourRecord } from '../TourRecordContext';

import { BlockAnchor } from './BlockAnchor';
import { SelfregLinks, SelfregLinksCount } from './selfreg/SelfregLinks';
import { TourFieldGrid } from './TourSectionPanel';

export function SelfregBlock() {
  const { detail } = useTourRecord();
  const { hasPerm } = usePermissions();
  // A link is a live credential (anyone holding it can join the tour): reading the links
  // needs console_tours_manage; a viewer sees how many there are.
  const canSeeLinks = hasPerm('console_tours_manage');

  return (
    <BlockAnchor id="selfreg" label="Self-registration">
      <Box sx={{ display: 'grid', gap: 0.5 }}>
        <Box data-block-heading sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: HEADING_ROW_PX, minWidth: 0 }}>
          <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600, flex: 1 }}>
            Self-registration
          </Typography>
        </Box>
        <TourFieldGrid keys={['allow_selfreg', 'selfreg_verify_email', 'selfreg_verify_phone']} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <SyncIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
          Imports reset “Verify email” and “Verify phone” to off on every run unless the XML sets them.
        </Typography>
        {canSeeLinks ? <SelfregLinks detail={detail} /> : <SelfregLinksCount count={detail.selfreg_link_count} />}
      </Box>
    </BlockAnchor>
  );
}
